import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { from, of } from 'rxjs';
import { catchError, filter, switchMap, tap } from 'rxjs/operators';
import * as AuthActions from '../auth/auth.actions';
import { PushNotificationsService } from '../../core/services/push-notifications.service';

/**
 * PushNotificationsEffects — wires FCM token registration to the auth lifecycle.
 *
 * - On `setAccessToken` (non-null) → `pushNotifications.initAndRegister()`
 * - On `signOutSuccess` + `setAccessToken(null)` → `pushNotifications.unregister()`
 *
 * No new actions are dispatched — fire-and-forget side effects. Logging is
 * delegated to the service itself (which we kept quiet to avoid spamming the
 * console with success noise).
 */
@Injectable()
export class PushNotificationsEffects {
    private actions$ = inject(Actions);
    private push = inject(PushNotificationsService);

    registerOnLogin$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(AuthActions.setAccessToken),
                filter(({ accessToken }) => !!accessToken),
                tap(() => {
                    from(this.push.initAndRegister()).pipe(
                        catchError((err) => {
                            console.warn('[PushEffects] initAndRegister error:', err);
                            return of(null);
                        })
                    ).subscribe();
                })
            ),
        { dispatch: false }
    );

    unregisterOnLogout$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(AuthActions.signOutSuccess, AuthActions.setAccessToken),
                filter((action) => {
                    if (action.type === AuthActions.signOutSuccess.type) return true;
                    return !((action as any)?.accessToken);
                }),
                switchMap(() =>
                    from(this.push.unregister()).pipe(
                        catchError((err) => {
                            console.warn('[PushEffects] unregister error:', err);
                            return of(null);
                        })
                    )
                )
            ),
        { dispatch: false }
    );
}
